import { useState, ChangeEvent, useEffect } from 'react';
import Button from '../Button/Button';
import Box from '../../Layout/Box/Box';
import styles from './MultiInputFile.module.scss';

export type MultiInputFileProps = {
  // Add any additional props as needed
  id: string;
  name: string;
  placeholder: string;
  onChange: (files: File[] | null, name: string) => void;
  allowedExtensions?: string[];
};

const MultiInputFile: React.FC<MultiInputFileProps> = (props: MultiInputFileProps) => {
  const [files, setFiles] = useState<File[] | null>(null);
  const { id, name, onChange, placeholder, allowedExtensions } = props;
  const [textShown, setTextShown] = useState('');
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    onChange(files, name);
    if (files && files.length > 0) {
      if (files && files.length === 1) {
        setTextShown(`${files.length} file attached`);
      } else {
        setTextShown(`${files.length} files attached`);
      }
    } else {
      setTextShown(placeholder);
    }
  }, [files]);
  useEffect(() => {
    setTextShown(placeholder);
  }, [placeholder]);
  const openFileInput = () => {
    const fileInput = document.getElementById(`${id}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files;
    if (newFiles) {
      const invalidFiles = Array.from(newFiles).filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase() as string;
        return !allowedExtensions || !allowedExtensions.includes(extension);
      });

      if (invalidFiles.length > 0) {
        setHasError(true);
        setTextShown('Invalid file type. Please upload a PNG, JPG, or PDF.');
        // You can also set an error state in your component if needed.
        return;
      }
      setHasError(false);
      setFiles((existingFiles) => {
        if (existingFiles) {
          // Merge the new files with the existing files
          const updatedFiles: File[] = Array.from(existingFiles);
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < newFiles.length; i++) {
            const isNewFile = !updatedFiles.some(
              (existingFile) => existingFile.name === newFiles[i].name && existingFile.size === newFiles[i].size,
            );
            if (isNewFile) {
              updatedFiles.push(newFiles[i]);
            }
          }
          return updatedFiles;
        }
        return Array.from(newFiles);
      });
    }
  };

  const removeFile = (index: number) => {
    if (files) {
      const newFiles = [...files];
      newFiles.splice(index, 1);
      setFiles(newFiles.length > 0 ? newFiles : null);
    }
  };

  return (
    <div>
      <input type="file" name={name} id={id} multiple style={{ display: 'none' }} onChange={handleFileSelect} />
      <Box ai="center" gap="sm" jc="fs" height="auto">
        <Button
          onClick={() => {
            openFileInput();
          }}
          id={`button-${id}`}
          type="button"
          label="Add Attachment"
          color="secondary"
          height="36px"
        />
        <div className={hasError ? styles.TextError : undefined}>{textShown}</div>
      </Box>

      <Box flx="col" gapSize="8px" pt="md" height="auto">
        {files &&
          Array.from(files).map((file, index) => (
            <Box ai="center" gap="sm" jc="fs">
              <Button
                id={`delete-${id}-${index}`}
                onClick={() => {
                  removeFile(index);
                }}
                type="button"
                icon="trash"
                label=""
                iconWidth="16.5px"
                iconHeight="17px"
                width="16.5px"
                height="17px"
                color="none"
                iconLocation="left"
              />

              <div style={{ fontSize: '16px', lineHeight: '24px', color: '#4D4D4D' }}>{file.name}</div>
            </Box>
          ))}
      </Box>
    </div>
  );
};

export default MultiInputFile;
